// menuConfig.js
// Pure functions that build Ant Design menu item arrays.
// No React state, no axios calls — just item construction.

import { Link } from 'react-router-dom';
import {
   ProjectOutlined,
   CloudOutlined,
   FileDoneOutlined,
   LogoutOutlined,
   EditOutlined,
   FileOutlined,
   TeamOutlined,
   HomeOutlined,
   ScheduleOutlined,
   CalendarOutlined,
   ProfileOutlined,
   CarryOutOutlined,
   ReadOutlined,
   BarChartOutlined,
   BookOutlined,
   NotificationOutlined,
   ControlOutlined,
   SolutionOutlined,
   ToTopOutlined,
   ClusterOutlined,
   UnorderedListOutlined,
   OrderedListOutlined,
   FormOutlined,
   IdcardOutlined,
   AccountBookOutlined,
   HistoryOutlined,
   AreaChartOutlined,
   DatabaseOutlined,
   VideoCameraOutlined,
   SettingOutlined,
} from '@ant-design/icons';
import Utils from '../../misc/Utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Wraps Ant Design's getItem signature */
export function getItem(label, key, icon, children) {
   return { key, icon, children, label };
}

function link(to, label, onClick) {
   return (
      <Link to={to} onClick={onClick}>
         <span>{label}</span>
      </Link>
   );
}

function clearSelectedTeacher() {
   localStorage.removeItem('selected-teacher');
   localStorage.removeItem('selected-teacher-name');
}

function microcredentialsItem({
   key,
   lessonTo = '#',
   lessonOnClick,
   homeworkTo = '#',
   homeworkOnClick,
   performanceTaskTo = '/microcredentials/performance-tasks',
   performanceTaskOnClick,
   writtenWorksTo = '/microcredentials/written-works',
   writtenWorksOnClick,
   assessmentTo,
   assessmentOnClick,
   managementTo,
   managementOnClick,
   teacherGuideTo = '#',
   teacherGuideOnClick,
   showTeacherGuide = true,
}) {
   const children = [];

   if (showTeacherGuide) {
      children.push(
         getItem(
            link(teacherGuideTo, "Teacher's Guide", teacherGuideOnClick),
            `${key}-teacher-guide`,
            <ReadOutlined />
         )
      );
   }

   children.push(
      getItem(
         link(performanceTaskTo, 'Performance Task', performanceTaskOnClick),
         `${key}-performance-task`,
         <BookOutlined />
      ),
      getItem(
         link(writtenWorksTo, 'Written Works', writtenWorksOnClick),
         `${key}-written-works`,
         <BookOutlined />
      )
   );

   if (assessmentTo) {
      children.push(
         getItem(
            link(assessmentTo, 'Assessment', assessmentOnClick),
            `${key}-assessment`,
            <FileDoneOutlined />
         )
      );
   }

   if (managementTo) {
      children.push(
         getItem(
            link(managementTo, 'Course & Topic Management', managementOnClick),
            `${key}-management`,
            <SettingOutlined />
         )
      );
   }

   return getItem('Brainee Microcredentials', key, <ReadOutlined />, children);
}

// ---------------------------------------------------------------------------
// Shared / reusable items
// ---------------------------------------------------------------------------

export const logoutItem = (onConfirm) =>
   getItem(link('#', 'Logout', onConfirm), 'logout', <LogoutOutlined />);

export const homeItem = getItem(link('/home', 'Home'), '1', <HomeOutlined />);

// export const homeItem = getItem("Home", "sub-1", <HomeOutlined />, [
//   getItem(link("/home", "Home"), "2", <ScheduleOutlined />),
//   getItem(link("/announcement", "School Feed"), "3", <CalendarOutlined />),
// ]);

export const scheduleMenu = getItem('Schedule', 'sub0', <ScheduleOutlined />, [
   getItem(link('/classsched', 'Class Schedule'), '2', <ScheduleOutlined />),
   getItem(link('/lessoncalendar', 'Lesson Calendar'), '3',<CalendarOutlined />),
   getItem(link('/schoolcalendar', 'School Calendar'), '4', <CalendarOutlined />),
]);

export const reportCardMenu = getItem(
   'Report Card & Certificates',
   'sub1',
   <ProfileOutlined />,
   [
      getItem(link('/reportcards', 'Report Card'), '5', <ProfileOutlined />),
      getItem(link('/certificates', 'Certificate'), '6', <ProfileOutlined />),
   ]
);

export const attendanceMenu = getItem(
   'Attendance',
   'sub2',
   <CarryOutOutlined />,
   [
      getItem(
         link('/classroomattendance', 'Classroom'),
         '7',
         <ProfileOutlined />
      ),
      getItem(link('/periodattendance', 'Period'), '8', <ProfileOutlined />),
      getItem(link('/attendance', 'Gate'), '9', <ProfileOutlined />),
      getItem(
         'Report',
         'g0',
         <BarChartOutlined />,
         [
            getItem(
               link('classroomattendancerpt', 'Classroom Attendance'),
               '48'
            ),
            getItem(link('periodattendancerpt', 'Period Attendance'), '49'),
         ],
         'group'
      ),
   ]
);

export const managementMenuAdmin = getItem(
   'Management',
   'sub5',
   <ControlOutlined />,
   [
      getItem(
         link('/managestudentinfo', 'Student Information'),
         '31',
         <SolutionOutlined />
      ),
      getItem(
         link('/promotestudents', 'Promote Students'),
         '32',
         <ToTopOutlined />
      ),
      getItem(link('/subjects', 'Subjects'), '33', <EditOutlined />),
      getItem(
         link('/subjectgroups', 'Subject Groups'),
         '34',
         <ClusterOutlined />
      ),
      getItem(link('/sections', 'Sections'), '35', <UnorderedListOutlined />),
      getItem(link('/levels', 'Levels'), '36', <OrderedListOutlined />),
      getItem(
         link('/managestaffinfo', 'Staff Information'),
         '37',
         <TeamOutlined />
      ),
      getItem(
         link('/appinfogroups', 'Application Form Sections'),
         '38',
         <FormOutlined />
      ),
      getItem(
         link('/appinfofields', 'Application Form Fields'),
         '39',
         <FormOutlined />
      ),
      getItem(
         link('/configsettings', 'Configuration Settings'),
         '40',
         <SettingOutlined />
      ),
   ]
);

export const studentInfoItem = getItem(
   link('/managestudentinfo', 'Student Information'),
   '31',
   <IdcardOutlined />
);

// ---------------------------------------------------------------------------
// Brainee LMS sub-menus — vary by subdomain
// ---------------------------------------------------------------------------

const diagnosticGroup = (preLink, postLink) =>
   getItem(
      'Diagnostic Assessment',
      'g1',
      <CalendarOutlined />,
      [
         getItem(link(preLink, 'Pre Test'), '17'),
         getItem(link(postLink, 'Post Test'), '18'),
         getItem(link('/bdiagnostic/report', 'Reports'), '19'),
      ],
      'group'
   );

export function braineeLMSMenuForTeacher(subdomain, onUnavailable) {
   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);

   if (isSbu) {
      return [
         getItem(
            link('/homeworksubjects', 'Homework & Project'),
            '13',
            <ProjectOutlined />
         ),
         getItem(
            link('/masterysubjects', 'Brainee Mastery Assessment'),
            '15',
            <FileDoneOutlined />
         ),
         getItem(
            link('/masteryassessmentreport', 'Mastery Assessment Reports'),
            '16',
            <BarChartOutlined />
         ),
         diagnosticGroup(
            '/bdiagnostic/subjects?type=pre',
            '/bdiagnostic/subjects?type=post'
         ),
      ];
   }

   return [
      getItem(
         link('/subject', 'Lesson', clearSelectedTeacher),
         '10',
         <ReadOutlined />
      ),
      getItem(
         link(
            '/formativeassessment/subjects',
            'Formative Assessment',
            clearSelectedTeacher
         ),
         '11',
         <FileDoneOutlined />
      ),
      getItem(
         link(
            '/assessment/subjects',
            'Summative Assessment',
            clearSelectedTeacher
         ),
         '12',
         <FileDoneOutlined />
      ),
      getItem(
         link('/homeworksubjects', 'Homework & Project'),
         '13',
         <ProjectOutlined />
      ),
      getItem(
         link('#', 'Lesson Reports', onUnavailable),
         '14',
         <BarChartOutlined />
      ),
      getItem(
         link('/masterysubjects', 'Brainee Mastery Assessment'),
         '15',
         <FileDoneOutlined />
      ),
      getItem(
         link('/masteryassessmentreport', 'Mastery Assessment Reports'),
         '16',
         <BarChartOutlined />
      ),
      diagnosticGroup(
         '/bdiagnostic/subjects?type=pre',
         '/bdiagnostic/subjects?type=post'
      ),
   ];
}

export function braineeLMSMenuForStudent(subdomain, onUnavailable) {
   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);
   const o2oMode = Utils.determineOnlineOffline();

   const diagnosticStudent = getItem(
      'Diagnostic Test',
      'g1',
      <CalendarOutlined />,
      [
         getItem(
            link('/bdiagnostic/student/subjects?type=pre', 'Pre Test'),
            '13'
         ),
         getItem(
            link('/bdiagnostic/student/subjects?type=post', 'Post Test'),
            '14'
         ),
      ],
      'group'
   );

   if (isSbu) {
      return [
         getItem(
            link('/homeworksubjects', 'Homework & Project'),
            '13',
            <ProjectOutlined />
         ),
         getItem(
            link('/masterysubjects', 'Mastery Assessment'),
            '15',
            <FileDoneOutlined />
         ),
         getItem(
            link('/masteryassessmentreport', 'Mastery Assessment Reports'),
            '16',
            <BarChartOutlined />
         ),
         getItem(
            'Diagnostic Assessment',
            'g1',
            <CalendarOutlined />,
            [
               getItem(
                  link('/bdiagnostic/student/subjects?type=pre', 'Pre Test'),
                  '13'
               ),
               getItem(
                  link('/bdiagnostic/student/subjects?type=post', 'Post Test'),
                  '14'
               ),
            ],
            'group'
         ),
      ];
   }

   return [
      o2oMode === 'offline'
         ? getItem(link('#', 'Lesson', onUnavailable), '9', <ReadOutlined />)
         : getItem(link('/subject', 'Lesson'), '9', <ReadOutlined />),
      getItem(
         link('/studentformativeassessment/subjects', 'Formative Assessment'),
         '10',
         <FileDoneOutlined />
      ),
      getItem(
         link('/studentassessment/subjects', 'Summative Assessment'),
         '11',
         <FileDoneOutlined />
      ),
      getItem(
         link('/studentmasteryassessment', 'Mastery Assessment'),
         '12',
         <FileDoneOutlined />
      ),
      diagnosticStudent,
      getItem(
         link('/studenthomeworks/subjects', 'Homework & Project'),
         '15',
         <ProjectOutlined />
      ),
   ];
}

// ---------------------------------------------------------------------------
// E-Library helpers
// ---------------------------------------------------------------------------

const lcccBranches = [
   {
      label: 'Grade School',
      url: 'https://lcccaloocangs-library.braineeph.com',
   },
   {
      label: 'Junior High School',
      url: 'https://lcccaloocanjhs-library.braineeph.com',
   },
   {
      label: 'Senior High School',
      url: 'https://lcccaloocan-library.braineeph.com',
   },
];

export function eLibraryMenu(subdomain, onELibraryClick, startKey = 26) {
   if (subdomain === 'lcccaloocan') {
      return getItem(
         'Brainee E-Library',
         'sub3',
         <BookOutlined />,
         lcccBranches.map(({ label, url }, i) =>
            getItem(
               link('#', label, () => window.open(url, '_blank').focus()),
               String(startKey + i)
            )
         )
      );
   }
   return getItem(
      link('#', 'Brainee E-Library', onELibraryClick),
      '29',
      <BookOutlined />
   );
}

// ---------------------------------------------------------------------------
// Role-based menu builders
// ---------------------------------------------------------------------------

/**
 * Menu for a student with status '0' (enrollment-only access).
 */
export function buildEnrollmentStudentMenu(onLogout) {
   return [
      getItem(
         link('/studentassessment', 'Examination'),
         '1',
         <FileDoneOutlined />
      ),
      logoutItem(onLogout),
   ];
}

/**
 * Menu for a parent with status '0'.
 */
export function buildEnrollmentParentMenu(onLogout) {
   return [
      getItem(
         link('/studentprofile', 'Update Student Information'),
         '2',
         <EditOutlined />
      ),
      getItem(
         link('/studentfees', 'Tuition Fee Assessment'),
         '3',
         <FileOutlined />
      ),
      logoutItem(onLogout),
   ];
}

/**
 * Menu for Registrar / Cashier roles.
 */
export function buildRegistrarMenu(onLogout) {
   return [
      getItem(
         link('/applicantlist', 'Student Applicants'),
         '1',
         <TeamOutlined />
      ),
      logoutItem(onLogout),
   ];
}

/**
 * Menu for Guidance role.
 */
export function buildGuidanceMenu(onLogout) {
   return [
      getItem(
         link('/applicantlist', 'Student Applicants'),
         '1',
         <TeamOutlined />
      ),
      getItem(
         link('/assessment', 'Examination (LMS)'),
         '2',
         <FileDoneOutlined />
      ),
      logoutItem(onLogout),
   ];
}

/**
 * Menu for Student role (full access).
 */
export function buildStudentMenu({
   subdomain,
   hasOA,
   hasBraineeConference,
   hasMicrocredentials,
   onUnavailable,
   onELibraryClick,
   onLiveClassJoin,
   onLogout,
}) {
   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);
   const o2oMode = Utils.determineOnlineOffline();
   const scheduleItems = getItem('Schedule', 'sub0', <ScheduleOutlined />, [
      getItem(link('/classsched', 'Class Schedule'), '2', <CalendarOutlined />),
      getItem(
         link('/lessoncalendar', 'Lesson Calendar'),
         '3',
         <CalendarOutlined />
      ),
      getItem(
         link('/schoolcalendar', 'School Calendar'),
         '4',
         <CalendarOutlined />
      ),
   ]);

   const reportCardItems = getItem(
      'Report Card & Certificates',
      'sub1',
      <ProfileOutlined />,
      [
         getItem(
            link('/studentreportcard', 'Report Card'),
            '5',
            <CalendarOutlined />
         ),
         getItem(
            link('/studentcertificate', 'Certificate'),
            '6',
            <CalendarOutlined />
         ),
      ]
   );

   const attendanceItems = getItem(
      'Student Attendance',
      'sub2',
      <ProfileOutlined />,
      [
         getItem(
            link('/classroomattendancerpt', 'Classroom'),
            '7',
            <CalendarOutlined />
         ),
         getItem(link('/attendance', 'Gate'), '8', <CalendarOutlined />),
      ]
   );

   const lmsItems = braineeLMSMenuForStudent(subdomain, onUnavailable);
   const microcredentials = microcredentialsItem({
      key: 'microcredentials',
      showTeacherGuide: false,
      teacherGuideOnClick: onUnavailable,
      lessonOnClick: onUnavailable,
      homeworkTo: '/studentmicrocredentials/homeworks/subjects',
      homeworkOnClick: clearSelectedTeacher,
      performanceTaskTo: '/studentmicrocredentials/performance-tasks/subjects',
      performanceTaskOnClick: clearSelectedTeacher,
      writtenWorksTo: '/studentmicrocredentials/written-works/subjects',
      writtenWorksOnClick: clearSelectedTeacher,
      assessmentTo: '/studentmicrocredsassessment/subjects',
   });
   const braineeResourcesItem = buildBraineeResourcesItem(
      subdomain,
      onUnavailable
   );
   const eLib = eLibraryMenu(subdomain, onELibraryClick, 17);

   return [
      homeItem,
      scheduleItems,
      reportCardItems,
      attendanceItems,
      getItem('Brainee LMS', 'sub3', <CloudOutlined />, lmsItems),
      ...(!isSbu && hasMicrocredentials ? [microcredentials] : []),
      hasBraineeConference &&
         getItem(
            link('#', 'Brainee Live Class', onLiveClassJoin),
            '50',
            <VideoCameraOutlined />
         ),
      braineeResourcesItem,
      eLib,
      logoutItem(onLogout),
   ];
}

/**
 * Menu for Parent role.
 */
export function buildParentMenu({
   subdomain,
   hasOA,
   hasEmergencySheet,
   onUnavailable,
   onLogout,
}) {
   const isSbuOrMts = [
      'sbuibedmanila',
      'sbuibedrizal',
      'mts-bacoor',
      'mts-gentri',
   ].includes(subdomain);

   const studentRecordsChildren = isSbuOrMts
      ? [
           getItem(
              link('#', 'Student Information', onUnavailable),
              '12',
              <ProfileOutlined />
           ),
           getItem(
              link('#', 'Payment History', onUnavailable),
              '13',
              <HistoryOutlined />
           ),
        ]
      : [
           hasEmergencySheet &&
              getItem(
                 link('/studentemergencyinfo', 'Emergency Information'),
                 '12',
                 <ProfileOutlined />
              ),
           getItem(
              link('/soa', 'Statement of Accounts'),
              '12.1',
              <AccountBookOutlined />
           ),
           getItem(
              link('#', 'Payment History', onUnavailable),
              '13',
              <HistoryOutlined />
           ),
        ].filter(Boolean);

   return [
      homeItem,
      hasOA &&
         getItem(
            link('/admission', 'Online Enrollment'),
            '3',
            <CloudOutlined />
         ),
      getItem('Schedule', 'sub0', <ScheduleOutlined />, [
         getItem(
            link('/classsched', 'Class Schedule'),
            '4',
            <CalendarOutlined />
         ),
         getItem(
            link('/lessoncalendar', 'Lesson Calendar'),
            '5',
            <CalendarOutlined />
         ),
         getItem(
            link('/schoolcalendar', 'School Calendar'),
            '6',
            <CalendarOutlined />
         ),
      ]),
      getItem('Report Card & Certificates', 'sub1', <CalendarOutlined />, [
         getItem(
            link('/studentreportcard', 'Report Card'),
            '7',
            <CalendarOutlined />
         ),
         getItem(
            link('/studentcertificate', 'Certificate'),
            '8',
            <CalendarOutlined />
         ),
      ]),
      getItem('Student Attendance', 'sub2', <ProfileOutlined />, [
         getItem(
            link('/classroomattendancerpt', 'Classroom'),
            '9',
            <BarChartOutlined />
         ),
         getItem(
            link('/periodattendancerpt', 'Period'),
            '9.1',
            <BarChartOutlined />
         ),
         getItem(link('/attendance', 'Gate'), '10', <CalendarOutlined />),
      ]),
      getItem(
         link('/studenthomeworks/subjects', 'Homework & Project'),
         '11',
         <ProjectOutlined />
      ),
      getItem(
         'Student Records',
         'sub3',
         <AccountBookOutlined />,
         studentRecordsChildren
      ),
      logoutItem(onLogout),
   ].filter(Boolean);
}

/**
 * Menu for Teacher / Admin / Super Admin / Co-Teacher roles.
 */
export function buildStaffMenu({
   userRole,
   subdomain,
   hasZoom,
   hasBraineeConference,
   hasMicrocredentials,
   ZoomIcon,
   onUnavailable,
   onELibraryClick,
   onLiveClassPickerOpen,
   onZoomPickerOpen,
   onLogout,
}) {
   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);
   const isCoTeacher = userRole === 'Co-Teacher';
   const isSuperAdmin = userRole === 'Super Admin';
   const isAdmin = userRole === 'Admin';
   const isTeacher = userRole === 'Teacher';

   const braineeResourcesItem = buildBraineeResourcesItem(
      subdomain,
      onUnavailable
   );

   const lmsItemsTeacher = braineeLMSMenuForTeacher(subdomain, onUnavailable);
   const lmsItemsCoTeacher = [
      getItem(link('/subject', 'Lesson'), '9', <ReadOutlined />),
      getItem(
         link('/formativeassessment/subjects', 'Formative Assessment'),
         '10',
         <FileDoneOutlined />
      ),
      getItem(
         link('/assessment/subjects', 'Summative Assessment'),
         '11',
         <FileDoneOutlined />
      ),
      getItem(
         link('/homeworksubjects', 'Homework & Project'),
         '12',
         <ProjectOutlined />
      ),
      getItem(
         link('/masterysubjects', 'Brainee Mastery Assessment'),
         '13',
         <FileDoneOutlined />
      ),
      getItem(
         link('#', 'Lesson Reports', onUnavailable),
         '14',
         <BarChartOutlined />
      ),
      getItem(
         link('/masteryassessmentreport', 'Mastery Assessment Reports'),
         '14.1',
         <BarChartOutlined />
      ),
   ];

   const eLib = eLibraryMenu(subdomain, onELibraryClick, 26);

   const menu = [
      homeItem,
      scheduleMenu,
      !isCoTeacher && reportCardMenu,
      attendanceMenu,
   ].filter(Boolean);

   // Brainee LMS menus
   if (!isCoTeacher && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(
         getItem('Brainee LMS', 'sub3', <CloudOutlined />, lmsItemsTeacher)
      );
   }

   if (isCoTeacher) {
      menu.push(
         getItem('Brainee LMS', 'sub2', <CloudOutlined />, lmsItemsCoTeacher)
      );
   }

   // Unified Microcredentials menu (appears once)
   if (hasMicrocredentials) {
      const mcProps = { key: 'microcredentials' };

      mcProps.teacherGuideOnClick = onUnavailable;
      mcProps.lessonOnClick = onUnavailable;
      mcProps.homeworkTo = '/microcredentials/homeworks';
      mcProps.homeworkOnClick = clearSelectedTeacher;

      // Teacher/Admin roles get assessment and performance task access
      if (!isCoTeacher && (isSuperAdmin || isAdmin || isTeacher)) {
         mcProps.teacherGuideTo = '/microcredentials/teacher-guide';
         mcProps.teacherGuideOnClick = clearSelectedTeacher;
         mcProps.performanceTaskTo = '/microcredentials/performance-tasks';
         mcProps.performanceTaskOnClick = clearSelectedTeacher;
         mcProps.writtenWorksTo = '/microcredentials/written-works';
         mcProps.writtenWorksOnClick = clearSelectedTeacher;
         mcProps.assessmentTo = '/microcredsassessment/subjects';
         mcProps.assessmentOnClick = clearSelectedTeacher;
      }

      // Super admin also gets management link
      if (isSuperAdmin) {
         mcProps.managementTo = '/microcredentials-management';
         mcProps.managementOnClick = clearSelectedTeacher;
      }

      menu.push(microcredentialsItem(mcProps));
   }

   if (hasBraineeConference && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(
         getItem('Brainee Video Conference', 'sub6', <VideoCameraOutlined />, [
            getItem(
               link('#', 'Brainee Live Class Picker', onLiveClassPickerOpen),
               '19.1',
               <VideoCameraOutlined />
            ),
            getItem(
               link('#', 'Brainee Meeting Room', onUnavailable),
               '19.2',
               <VideoCameraOutlined />
            ),
         ])
      );
   }

   if (!isSbu) {
      menu.push(braineeResourcesItem);
   }

   menu.push(eLib);

   if (!isCoTeacher) {
      menu.push(
         getItem(
            link('/notice', 'Notice Board'),
            '30',
            <NotificationOutlined />
         )
      );
   }

   if (isSuperAdmin) {
      menu.push(managementMenuAdmin);
   }

   if (isAdmin || isTeacher) {
      menu.push(studentInfoItem);
   }

   if (isSuperAdmin || isAdmin) {
      menu.push(
         getItem(
            link('/surveyassign', 'Survey Assignment'),
            '42',
            <AreaChartOutlined />
         )
      );
   }

   if (hasZoom && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(
         getItem(link('#', 'Zoom Picker', onZoomPickerOpen), '44', <ZoomIcon />)
      );
   }

   menu.push(logoutItem(onLogout));
   return menu;
}

// ---------------------------------------------------------------------------
// Brainee Resources (access-controlled)
// ---------------------------------------------------------------------------

export function buildBraineeResourcesItem(subdomain, onUnavailable) {
   const isAllowed = Utils.hasBraineeResourcesAccess(subdomain);
   return getItem(
      link(
         isAllowed ? '/brainee-resources' : '#',
         'Brainee Resources',
         isAllowed ? undefined : onUnavailable
      ),
      '41',
      <DatabaseOutlined />
   );
}

// ---------------------------------------------------------------------------
// Sudeco special case
// ---------------------------------------------------------------------------

export function buildSudecoMenu(userRole, onLogout) {
   if (['Registrar', 'Guidance', 'Cashier'].includes(userRole)) return [];

   const menu = [homeItem];

   if (['Super Admin', 'Admin', 'Teacher'].includes(userRole)) {
      menu.push(
         getItem(
            link('/classsched', 'Class Schedule'),
            '2',
            <ScheduleOutlined />
         ),
         getItem(link('/reportcards', 'Report Card'), '5', <ProfileOutlined />)
      );
   }

   menu.push(logoutItem(onLogout));
   return menu;
}




